import React, { useCallback, useMemo, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView, placeholder as placeholderExtension } from "@codemirror/view";
import { useAutocompleteEntities } from "../../../hooks/useAutocompleteEntities";
import { createAutocompleteExtension } from "./autocompleteSource";
import "./AutocompleteTextarea.css";

/**
 * General-purpose autocomplete textarea component with optional form wrapper and submit button
 * 
 * @param {string} value - Controlled value (optional if using internal state with onSendMessage)
 * @param {Function} onChange - onChange handler: (value: string) => void (optional if using onSendMessage)
 * @param {Array<string>} entityTypes - Entity types to include: ['players', 'metrics', 'positions', 'playerGroups', 'sessions']
 * @param {number} maxSuggestions - Maximum number of suggestions (default: 10)
 * @param {number} minChars - Minimum characters before showing suggestions (default: 2)
 * @param {number} rows - Number of rows (default: 4)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether textarea is disabled
 * @param {Function} onEnter - Optional callback when Enter is pressed (without Shift)
 * @param {Object} style - Additional styles for the textarea wrapper
 * @param {string} className - Additional CSS class for the textarea wrapper
 * @param {Function} onSendMessage - Optional callback for form submission: (message: string, sender: string) => void. When provided, component manages its own state and includes form/button.
 * @param {boolean} isTyping - Whether the component is in a typing/loading state (disables input and button)
 * @param {boolean} showSubmitButton - Whether to show a submit button (default: false, or true if onSendMessage is provided)
 * @param {string} buttonText - Text for the submit button (default: "Send")
 * @param {string} buttonClassName - CSS class for the submit button
 * @param {string} containerClassName - CSS class for the form container (when using form wrapper)
 * @param {Object} containerStyle - Additional styles for the form container
 * @param {Object} textareaStyle - Additional styles specifically for the textarea (merged with style)
 * @param {string} sender - Sender identifier passed to onSendMessage (default: "user")
 * @param {string} theme - Theme to use: 'search' (default) or 'textarea'
 * @param {string} ariaLabel - Optional accessible name for the editor's textbox
 * @param {Function} onPaste - Optional paste interceptor: (event: ClipboardEvent, view: EditorView) => boolean | void. Return true (or call event.preventDefault()) to suppress the default text paste; return falsy to let text paste proceed. Wrap in useCallback for stability.
 * @param {Object} ...rest - Other props passed to underlying CodeMirror component
 */
export const AutocompleteTextarea = (props) => {
	const {
		value: controlledValue,
		onChange: controlledOnChange,
		entityTypes = ["players", "metrics", "positions", "playerGroups", "treatments", "treatmentApplicationTime"],
		maxSuggestions = 5,
		minChars = 3,
		rows = 4,
		placeholder = "",
		disabled = false,
		onEnter = null,
		style = {},
		className = "",
		onSendMessage = null,
		isTyping = false,
		showSubmitButton = null, // null means auto-detect based on onSendMessage
		buttonText = "Send",
		buttonClassName = "",
		containerClassName = "",
		containerStyle = {},
		textareaStyle = {},
		sender = "user",
		theme = "search", // Default theme is 'search'
		onPaste = null,
		ariaLabel = null,
		...rest
	} = props;

	// Internal state management when onSendMessage is provided
	const [internalValue, setInternalValue] = useState("");
	const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;
	const value = isControlled ? controlledValue : internalValue;
	const onChange = isControlled ? controlledOnChange : setInternalValue;
	const shouldShowButton = showSubmitButton !== null ? showSubmitButton : (onSendMessage !== null);
	
	const editorRef = useRef(null);
	const submitHandlerRef = useRef(null);
	const onChangeRef = useRef(null);
	const onPasteRef = useRef(null);
	const entities = useAutocompleteEntities(entityTypes);

	const handleSubmit = (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}
		if (onSendMessage && value.trim()) {
			onSendMessage(value.trim(), sender);
			if (!isControlled) {
				setInternalValue("");
			}
		}
	};

	const handleEnterPress = () => {
		if (onSendMessage) {
			handleSubmit();
		} else if (onEnter) {
			onEnter();
		}
	};

	// Callbacks are reached through refs so the extensions below don't have to depend on
	// their identity. Callers pass inline arrows, and depending on those identities makes
	// CodeMirror reconfigure itself on every render, which loops into React error #185.
	submitHandlerRef.current = handleEnterPress;
	onChangeRef.current = onChange;
	onPasteRef.current = onPaste;

	const hasEnterHandler = Boolean(onEnter || onSendMessage);
	const hasPasteHandler = Boolean(onPaste);

	const height = useMemo(() => {
		return `${rows * 21 + 16}px`;
	}, [rows]);

	const extensions = useMemo(() => {
		// Search theme styles (default)
		const searchTheme = {
			"&": {
				fontSize: "14px",
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				backgroundColor: "#fff",
				border: "1px solid #ccc",
				borderRadius: "0.25rem",
				padding: "0.75rem 1.25rem",
				minHeight: height,
				boxShadow: "0 0.125rem 0.5rem rgba(0, 0, 0, 0.1)",
				transition: "border 0.3s, box-shadow 0.3s",
			},
			"&.cm-focused": {
				borderColor: "#6658f6",
				borderRightWidth: "1px !important",
				outline: "none",
				boxShadow: "0 0 0.5rem rgba(102, 88, 246, 0.5)",
			},
			".cm-content": {
				padding: "4px 0",
				minHeight: height,
				lineHeight: "1.5715",
				fontFamily: 'inherit',
				textAlign: "left",
			},
			".cm-line": {
				padding: "0",
				textAlign: "left",
			},
			".cm-scroller": {
				overflow: "auto",
			},
		};

		// Textarea theme styles
		const textareaTheme = {
			"&": {
				fontSize: "14px",
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				backgroundColor: "#fff",
				border: "1px solid #d9d9d9",
				borderRadius: "2px",
				padding: "0",
				height: height, // Fixed height at initial size - doesn't grow
				boxShadow: "none",
				transition: "border-color 0.3s, box-shadow 0.3s",
				overflow: "hidden", // Hide overflow on editor, let scroller handle it
				display: "flex",
				flexDirection: "column",
			},
			"&.cm-focused": {
				borderColor: "#40a9ff",
				borderRightWidth: "1px !important",
				outline: "none",
				boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)",
			},
			".cm-content": {
				padding: "4px 11px",
				// Content can grow beyond editor height to trigger scrolling
				lineHeight: "1.5715",
				fontFamily: 'inherit',
				textAlign: "left",
				maxHeight: "400px", // Limit maximum scrollable content height
			},
			".cm-line": {
				padding: "0",
				textAlign: "left",
			},
			".cm-scroller": {
				overflowY: "auto", // Explicitly enable vertical scrolling
				overflowX: "hidden", // Prevent horizontal scrolling
				height: height, // Fixed height matching editor - scrolling happens when content exceeds this
				flex: "1 1 auto", // Allow scroller to fill available space
			},
		};

		// Select theme based on prop
		const selectedTheme = theme === "textarea" ? textareaTheme : searchTheme;

		const extensionsList = [
			EditorView.theme(selectedTheme),
			EditorView.editable.of(!disabled && !isTyping),
			EditorView.lineWrapping,
		];

		if (placeholder) {
			extensionsList.push(placeholderExtension(placeholder));
		}

		// The accessible name of the editor's textbox. A placeholder is not a name.
		if (ariaLabel) {
			extensionsList.push(EditorView.contentAttributes.of({ "aria-label": ariaLabel }));
		}

		if (hasEnterHandler) {
			extensionsList.push(
				EditorView.domEventHandlers({
					keydown: (event, view) => {
						if (event.key === "Tab") {
							return false;
						}
						if (event.key === "Enter" && !event.shiftKey) {
							event.preventDefault();
							submitHandlerRef.current?.();
							return true;
						}
						return false;
					},
				})
			);
		}

		// Separate from the keydown handler above: that block is gated behind
		// (onEnter || onSendMessage), so nesting paste there would silently couple
		// it to onEnter being set. Register paste independently.
		if (hasPasteHandler) {
			extensionsList.push(
				EditorView.domEventHandlers({
					paste: (event, view) => {
						const handled = onPasteRef.current?.(event, view);
						if (handled) {
							event.preventDefault();
							return true;
						}
						return false;
					},
				})
			);
		}

		if (entities && entities.length > 0) {
			const autocompleteExtensions = createAutocompleteExtension(entities, maxSuggestions, minChars);
			extensionsList.push(...autocompleteExtensions);
		}

		return extensionsList;
	}, [entities, maxSuggestions, minChars, height, disabled, isTyping, placeholder, ariaLabel, hasEnterHandler, hasPasteHandler, theme]);

	// Must keep a stable identity: @uiw/react-codemirror re-dispatches a full
	// StateEffect.reconfigure whenever its `onChange` prop changes.
	const handleChange = useCallback((newValue) => {
		onChangeRef.current?.(newValue);
	}, []);

	const textareaElement = (
		<div 
			className={`autocomplete-textarea-wrapper ${theme === "textarea" ? "theme-textarea" : "theme-search"} ${className}`} 
			style={{ ...style, ...textareaStyle }}
		>
			<CodeMirror
				ref={editorRef}
				value={value || ""}
				onChange={handleChange}
				extensions={extensions}
				basicSetup={false}
				{...rest}
			/>
		</div>
	);

	// If form wrapper and button are needed
	if (shouldShowButton && onSendMessage) {
		return (
			<form 
				onSubmit={handleSubmit}
				className={containerClassName}
				style={containerStyle}
			>
				{textareaElement}
				<button
					type="submit"
					className={buttonClassName}
					disabled={isTyping || disabled || !value.trim()}
				>
					{buttonText}
				</button>
			</form>
		);
	}

	// Otherwise, just return the textarea
	return textareaElement;
};

export default AutocompleteTextarea;

